import React, { useCallback, useMemo, useState } from "react";
import { Modal, Input } from "antd";
import { useTranslation } from "react-i18next";
import { Checkbox } from "antd";
import * as R from "ramda";
import Fuse from "fuse.js";
import Masonry from "react-responsive-masonry";

import { removeAccents } from "../../../../../utils/strings";
import Button from "../../../../../components/Button";

import { StyledCheckbox } from "./FilterComponents";

import styled from "styled-components/macro";
const Label = styled.span`
  color: ${({ isSelected, theme }) => isSelected && theme["@primary"]};
`;

const Initial = styled.h4`
  font-weight: bold;
  margin-bottom: 10px;
`;

const InitialsWrapper = styled.div`
  padding: 5px;
`;

const initials = R.compose(
  R.uniq,
  R.map(
    R.pipe(
      R.prop("label"),
      R.head,
      removeAccents
    )
  )
);

const sortOptions = R.sortBy(
  R.compose(
    removeAccents,
    R.toLower,
    R.prop("label")
  )
);

const filterBySearch = (searchText, options) => {
  if (!searchText) return options;

  const fuse = new Fuse(options, {
    threshold: 0.2,
    isCaseSensitive: true,
    minMatchCharLength: 2,
    keys: [
      {
        name: "label",
        weight: 0.4
      }
    ]
  });

  return fuse.search(searchText);
};

const OptionsModal = ({
  grouped,
  loading,
  onClose,
  options,
  placeholder,
  setValues,
  sorted,
  title,
  values,
  visible
}) => {
  const { t } = useTranslation();
  const [filtered, setFiltered] = useState(options);
  const [selectedOptions, setSelectedOptions] = useState(values);
  const handleClose = useCallback(() => (!loading ? onClose() : undefined), [
    loading,
    onClose
  ]);

  const handleOk = useCallback(() => {
    setValues(selectedOptions);
    onClose();
  }, [onClose, selectedOptions, setValues]);

  const filteredOptions = useMemo(
    () => (grouped || sorted ? sortOptions(filtered) : filtered),
    [filtered, grouped, sorted]
  );

  const optionsInitials = useMemo(() => grouped && initials(filteredOptions), [
    filteredOptions,
    grouped
  ]);

  const mappedFilteredOptions = useMemo(
    () =>
      R.map(
        option => ({
          ...option,
          selected: R.includes(R.prop("value", option), selectedOptions),
          parseLabel: removeAccents(option.label)
        }),
        filteredOptions
      ),
    [filteredOptions, selectedOptions]
  );

  return (
    <Modal
      closable={!loading}
      destroyOnClose
      footer={[
        <Button
          key="close"
          type="quaternary"
          disabled={loading}
          onClick={handleClose}
        >
          {t("global:cancel")}
        </Button>,
        <Button
          key="create"
          loading={loading}
          type="primary"
          onClick={handleOk}
        >
          {t("global:accept")}
        </Button>
      ]}
      keyboard={false}
      maskClosable={false}
      onCancel={handleClose}
      visible={visible}
      width={900}
      title={title}
    >
      <Input.Search
        style={{ height: 44 }}
        onChange={ev => {
          setFiltered(filterBySearch(ev.target.value, options));
        }}
        placeholder={placeholder}
      />
      <Checkbox.Group
        style={{ width: "100%", padding: 15 }}
        onChange={setSelectedOptions}
        values={options}
        defaultValue={values}
      >
        <Masonry columnsCount={3}>
          {optionsInitials
            ? optionsInitials.map(initial => (
                <InitialsWrapper>
                  <Initial>{initial}</Initial>
                  {mappedFilteredOptions.map(
                    ({ label, selected, value, parseLabel }) =>
                      parseLabel[0] === initial && (
                        <StyledCheckbox
                          key={value}
                          value={value}
                          bold={selected}
                        >
                          <Label isSelected={selected}>{label}</Label>
                        </StyledCheckbox>
                      )
                  )}
                </InitialsWrapper>
              ))
            : mappedFilteredOptions.map(({ label, selected, value }) => (
                <StyledCheckbox value={value} bold={selected}>
                  <Label isSelected={selected}>{label}</Label>
                </StyledCheckbox>
              ))}
        </Masonry>
      </Checkbox.Group>
    </Modal>
  );
};

export default OptionsModal;
